
import _ from "lodash";
import mixins from "@/@core/mixins";
import MixinsUtilities from "@/@modules/fseg/services/MixinsUtilities";
import ContactEdit from "@/@modules/fseg/modals/ContactEdit.vue";
import FsegLayout from "@/@modules/fseg/components/Layout.vue";
import IbanEdit from "@/@modules/fseg/modals/IbanEdit.vue";
import IbanEditResultMsg from "@/@modules/fseg/modals/IbanEditResultMsg.vue";
import WorkerHeader from "@/@modules/fseg/components/WorkerHeader.vue";
import WorkerSidebar from "@/@modules/fseg/components/WorkerSidebar.vue";
import WorkerSignaletic from "@/@modules/fseg/components/WorkerSignaletic.vue";
import { Auth } from "@/@core";
import { CountryCodeService } from "@/@modules/fseg/services/CountryCodeService";
import { DateUtilities } from "@/@core/utilities/DateUtilities";
import { GraphqlService } from "@/@modules/fseg/services/GraphqlService";
import { SignalecticData } from "@/@modules/fseg/services/interfaces/worker/SignaleticData";
import { WorkerOverview } from "@/@modules/fseg/services/interfaces/worker/WorkerOverview";
import { worker } from "@/@modules/fseg/services/store/worker";

export default mixins(MixinsUtilities).extend({
  components: {
    FsegLayout,
    WorkerHeader,
    WorkerSidebar,
    WorkerSignaletic,
  },
  data() {
    return {
      isWorkerLoaded: false,
      hasData: false,
      sidebarTabs: [
        {
          label: this.$tc("fseg.signaletic.title"),
          route: `/worker/signaletic?id=${this.$route.query.id}`,
          id: "signaleticTab",
        },
      ],
      workerHeaderHeigt: "95px",
    };
  },
  async mounted() {
    if (!_.isEmpty(this.$route.query) && !("status" in this.$route.query) && this.hasWorkerRole) {
      this.$router.push("/worker/signaletic");
    }
    if (!CountryCodeService.countries || CountryCodeService.countries.length === 0) {
      await CountryCodeService.getCountries();
    }
    await this.getWorker();

    // TODO: check this...
    if (this.$route.query.status) {
      if (this.$route.query.status === "ok") {
        this.$modal.show(IbanEditResultMsg);
      } else if (this.$route.query.status === "ko" && this.$route.query.errorMessage) {
        this.$modal.show(IbanEditResultMsg, { error: true, errorMsg: this.$route.query.errorMessage });
      }
    }
  },
  computed: {
    country(): string {
      return this.hasData ? this.getCountry(worker.data!.address?.countryCode) : "";
    },
    derogAddrEndDate(): string {
      return worker.data!.derogationAddress?.endDate ?? "";
    },
    derogAddrStartDate(): string {
      return worker.data!.derogationAddress?.startDate ?? "";
    },
    derogationAddressPeriod(): string {
      return this.derogAddrStartDate || this.derogAddrEndDate
        ? `${
            this.derogAddrStartDate
              ? `${this.$t(
                  `fseg.signaletic.addresses.${
                    this.derogAddrStartDate && this.derogAddrEndDate ? "derogAddr_from" : "derogAddr_onlyFrom"
                  }`
                )} ${DateUtilities.display(this.derogAddrStartDate)}`
              : ""
          } ${
            this.derogAddrEndDate
              ? `${this.$t(
                  `fseg.signaletic.addresses.${
                    this.derogAddrEndDate && this.derogAddrStartDate ? "derogAddr_to" : "derogAddr_onlyTo"
                  }`
                )} ${DateUtilities.display(this.derogAddrEndDate)}`
              : ""
          }`
        : "";
    },
    derogationCountry(): string {
      return this.getCountry(worker.data!.derogationAddress?.countryCode);
    },
    firstNames(): string {
      return worker.data?.nativeWorker
        ? `${worker.data.nativeWorker.firstName ?? ""} ${worker.data.nativeWorker.secondName ?? ""} ${worker.data
            .nativeWorker.thirdName ?? ""}`
        : "";
    },
    fullName(): string {
      return worker.data?.nativeWorker
        ? `${worker.data.nativeWorker.lastName ?? ""}${this.firstNames ? `, ${this.firstNames}` : ""}`
        : "";
    },
    hasAgentRole(): boolean {
      return Auth.isInRole("AGENT");
    },
    hasWorkerRole(): boolean {
      return Auth.isInRole("WORKER");
    },
    iban(): string {
      const iban = worker.data?.foreignBankAccount?.iban;
      return iban ? this.getFormattedIban(iban) : "";
    },
    language(): string {
      return worker.data?.nativeWorker?.language ?? "";
    },
    signaleticDatas(): SignalecticData[] {
      return [
        {
          title: this.$tc("fseg.signaletic.personalData.title"),
          rows: [
            {
              label: this.$tc("fseg.signaletic.personalData.ssin"),
              value: this.ssin,
              id: "signaletic_personalData_ssin",
            },
            {
              label: this.$tc("fseg.signaletic.personalData.lastName"),
              value: worker.data!.nativeWorker?.lastName,
              id: "signaletic_personalData_lastName",
            },
            {
              label: this.$tc("fseg.signaletic.personalData.firstNames"),
              value: this.firstNames,
              id: "signaletic_personalData_firstNames",
            },
            {
              label: this.$tc("fseg.signaletic.personalData.language"),
              value: this.language ? this.$tc("fseg.signaletic.TRA_LANGUE_" + this.language) : "",
              id: "signaletic_personalData_language",
            },
            {
              label: this.$tc("fseg.signaletic.personalData.derogLang"),
              value: worker.data!.nativeWorker?.langueDerog
                ? `${this.$t("fseg.signaletic.TRA_LANGUE_" + worker.data!.nativeWorker.langueDerog)}`
                : "",
              id: "signaletic_personalData_derogationLanguage",
            },
            {
              label: this.$tc("fseg.signaletic.personalData.birthday"),
              value: worker.data!.nativeWorker?.birthday
                ? DateUtilities.display(worker.data!.nativeWorker.birthday)
                : "",
              id: "signaletic_personalData_birthday",
            },
            {
              label: this.$tc("fseg.signaletic.personalData.deceaseDate"),
              value: worker.data!.nativeWorker?.deceased
                ? DateUtilities.display(worker.data!.nativeWorker.deceased)
                : "",
              id: "signaletic_personalData_deceaseDate",
              hidden: !this.hasAgentRole,
            },
            {
              label: this.$tc("fseg.signaletic.personalData.sex"),
              value: worker.data!.nativeWorker?.sex
                ? `${this.$t("fseg.signaletic.sexType." + worker.data!.nativeWorker.sex)}`
                : "",
              id: "signaletic_personalData_sex",
              hidden: !this.hasAgentRole,
            },
          ],
        },
        {
          title: this.$tc("fseg.signaletic.addresses.title"),
          rows: [
            {
              label: this.$tc("fseg.signaletic.addresses.street"),
              value: worker.data!.address
                ? `${worker.data!.address.streetName ?? ""}${
                    worker.data!.address.streetNr ? `, ${worker.data!.address.streetNr}` : ""
                  } ${worker.data!.address.streetBox ?? ""}`
                : "",
              id: "signaletic_address_street",
            },
            {
              label: this.$tc("fseg.signaletic.addresses.locality"),
              value: worker.data!.address
                ? `${worker.data!.address.postalCode ?? ""}${
                    worker.data!.address.postalCode && worker.data!.address.municipality ? ", " : ""
                  } ${worker.data!.address.municipality ?? ""}`
                : "",
              id: "signaletic_address_locality",
            },
            {
              label: this.$tc("fseg.signaletic.addresses.country"),
              value: this.country,
              id: "signaletic_address_country",
            },
            {
              label: this.$tc("fseg.signaletic.addresses.modifDate"),
              value: worker.data!.address?.lastChanged ? DateUtilities.display(worker.data!.address.lastChanged) : "",
              id: "signaletic_address_modifDate",
              hidden: !this.hasAgentRole,
            },
            {
              label: this.$tc("fseg.signaletic.addresses.derogAddrPeriod"),
              value: this.derogationAddressPeriod,
              id: "signaletic_derogationAddressPeriod",
            },
            {
              label: this.$tc("fseg.signaletic.addresses.street"),
              value: worker.data!.derogationAddress
                ? `${worker.data!.derogationAddress.streetName ?? ""}${
                    worker.data!.derogationAddress.streetNr ? `, ${worker.data!.derogationAddress.streetNr}` : ""
                  } ${worker.data!.derogationAddress.streetBox ?? ""}`
                : "",
              id: "signaletic_derogationAddress_street",
            },
            {
              label: this.$tc("fseg.signaletic.addresses.locality"),
              value: worker.data!.derogationAddress
                ? `${worker.data!.derogationAddress.postalCode ?? ""}${
                    worker.data!.derogationAddress.postalCode && worker.data!.derogationAddress.municipality ? ", " : ""
                  } ${worker.data!.derogationAddress.municipality ?? ""}`
                : "",
              id: "signaletic_derogationAddress_locality",
            },
            {
              label: this.$tc("fseg.signaletic.addresses.country"),
              value: this.derogationCountry,
              id: "signaletic_derogationAddress_country",
            },
            {
              label: this.$tc("fseg.signaletic.addresses.modifDate"),
              value: worker.data!.derogationAddress?.lastChanged
                ? DateUtilities.display(worker.data!.derogationAddress?.lastChanged)
                : "",
              id: "signaletic_derogationAddress_modifDate",
            },
          ],
        },
        {
          title: this.$tc("fseg.signaletic.financialData.title"),
          edit: this.hasWorkerRole
            ? {
                icon: "fas fa-pencil-alt",
                fn: () => this.updateIban(),
              }
            : undefined,
          rows: [
            {
              label: this.$tc("fseg.signaletic.financialData.iban"),
              value: this.iban,
              id: "signaletic_financialData_iban",
              delete:
                this.hasWorkerRole && worker.data!.foreignBankAccount?.iban
                  ? {
                      icon: "fas fa-trash",
                      fn: () => this.updateIban({ isToBeDeleted: true }),
                    }
                  : undefined,
            },
            {
              label: this.$tc("fseg.signaletic.financialData.bic"),
              value: worker.data!.foreignBankAccount?.bic ?? "",
              id: "signaletic_financialData_bic",
            },
          ],
        },
        {
          title: this.$tc("fseg.signaletic.contactData.title"),
          edit: this.hasWorkerRole
            ? {
                icon: "fas fa-pencil-alt",
                fn: () => this.updateContact(),
              }
            : undefined,
          rows: [
            {
              label: this.$tc("fseg.signaletic.contactData.phone"),
              value: worker.data!.address?.telephone ?? "",
              id: "signaletic_contactData_phone",
            },
            {
              label: this.$tc("fseg.signaletic.contactData.mobilePhone"),
              value: worker.data!.derogationAddress?.telephone ?? "",
              id: "signaletic_contactData_mobilePhone",
            },
            {
              label: this.$tc("fseg.signaletic.contactData.email"),
              value: worker.data!.email ?? "",
              id: "signaletic_contactData_email",
            },
          ],
        },
      ];
    },
    ssin(): string {
      const ssin = worker.data?.nativeWorker?.ssin;
      return ssin ? this.getFormattedSsin(ssin, [6, 10], "-") : "";
    },
    workerId(): string | undefined {
      return this.$route.query.id as string | undefined;
    },
    workerOverview(): WorkerOverview {
      return {
        country: this.country,
        fullName: this.fullName,
        iban: this.iban,
        language: this.language,
        ssin: this.ssin,
      };
    },
  },
  methods: {
    getCountry(countryCode: number | string | null | undefined) {
      if (CountryCodeService.countries.length && countryCode) {
        const code = CountryCodeService.countries.find(c => c.lookupCode == countryCode);
        if (code) {
          return this.$i18n.locale === "nl" ? code.nLName : code.fRName;
        }
      }
      return "";
    },
    async getWorker() {
      if (!worker.data || worker.data.id != this.workerId) {
        if (this.hasAgentRole && this.workerId) {
          worker.data = await GraphqlService.getWorkerInfo({
            workerId: this.workerId as string,
            isAgent: this.hasAgentRole,
          });
        } else if (this.hasWorkerRole) {
          worker.data = await GraphqlService.getWorkerInfo({
            ssin: await Auth.getClaim("ssin"),
            isAgent: this.hasAgentRole,
          });
        }
      }
      this.hasData = !!worker.data;
      this.isWorkerLoaded = true;
    },
    async updateContact() {
      const isEdited = await this.$modal.show<boolean>(ContactEdit);
      if (isEdited) {
        this.$snackbar.show(this.$t("fseg.message.contactDataUpdated"), 2000);
      }
    },
    async updateIban({ isToBeDeleted } = { isToBeDeleted: false }) {
      await this.$modal.show(IbanEdit, { hasToDeleteIban: isToBeDeleted });
    },
  },
  watch: {
    workerId: {
      handler(value) {
        if (value && !this.hasAgentRole) {
          this.$router.push("/worker/signaletic");
        } else {
          this.getWorker();
        }
      },
    },
  },
});
